import { useCallback, useEffect, useState } from 'react'
import { Button, Group, Stack, Tabs, Text } from '@mantine/core'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import {
  defaultPermissionsForRole,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import type { MyPermissionsResponse, ProjectAISettingsResponse, ProjectDetailResponse } from '@/types/api'
import { AiContentTab } from './AiContentTab'
import { AiExceptionsTab } from './AiExceptionsTab'
import { AiResourcesTab } from './AiResourcesTab'
import { AiSettingsTab } from './AiSettingsTab'
import { AiLinkReplaceTab } from './AiLinkReplaceTab'
import { AiAdminApiActionsTab } from './AiAdminApiActionsTab'

const TAB_KEYS = [
  'settings',
  'admin-api',
  'content',
  'link-replace',
  'exceptions',
  'resources',
] as const
type AiTab = (typeof TAB_KEYS)[number]

function normalizeTab(raw: string | null): AiTab {
  if (raw === 'instructions' || raw === 'knowledge' || raw === 'variables') return 'content'
  if (raw && (TAB_KEYS as readonly string[]).includes(raw)) return raw as AiTab
  return 'settings'
}

export type ProjectAiAssistantWorkspaceProps = {
  projectId: string
  /** `page` — отдельный маршрут с ?tab=…; `embedded` — внутри настроек проекта, подвкладки только в state */
  variant?: 'page' | 'embedded'
}

export function ProjectAiAssistantWorkspace({
  projectId,
  variant = 'page',
}: ProjectAiAssistantWorkspaceProps) {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  const [project, setProject] = useState<ProjectDetailResponse | null>(null)
  const [settings, setSettings] = useState<ProjectAISettingsResponse | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [embeddedTab, setEmbeddedTab] = useState<AiTab>('settings')
  const [showAdvancedTabs, setShowAdvancedTabs] = useState(false)

  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])

  const effectivePerm = perm ?? defaultPermissionsForRole(user?.role)
  const canEdit = hasPermission(effectivePerm, 'manage_projects')

  const tabFromUrl = searchParams.get('tab')
  const activeTab: AiTab =
    variant === 'page' ? normalizeTab(tabFromUrl) : embeddedTab

  useEffect(() => {
    if (variant !== 'page') return
    if (!tabFromUrl) return
    if (!(TAB_KEYS as readonly string[]).includes(tabFromUrl)) {
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev)
          n.delete('tab')
          return n
        },
        { replace: true },
      )
    }
  }, [variant, tabFromUrl, setSearchParams])

  const setTab = (value: string | null) => {
    const nextTab = normalizeTab(value)
    if (variant === 'page') {
      const next = new URLSearchParams(searchParams)
      if (value && value !== 'settings') next.set('tab', value)
      else next.delete('tab')
      setSearchParams(next, { replace: true })
    } else {
      setEmbeddedTab(nextTab)
    }
  }

  const reloadAll = useCallback(async () => {
    if (!projectId) return
    setLoadError(null)
    try {
      const [p, ai] = await Promise.all([api.project(projectId), api.projectAI(projectId)])
      setProject(p)
      setSettings(ai)
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'Не удалось загрузить проект')
      setProject(null)
      setSettings(null)
    }
  }, [projectId])

  useEffect(() => {
    void reloadAll()
  }, [reloadAll, refreshKey])

  const bump = useCallback(() => setRefreshKey((k) => k + 1), [])

  const provider = (settings?.provider_type || 'openai').toLowerCase()
  const projectAiId = settings?.id || ''

  const backHref = `/settings?tab=projects&project=${encodeURIComponent(projectId)}&pt=ai`

  if (!projectId) {
    return <Text size="sm">Не указан проект</Text>
  }

  return (
    <Stack gap="md">
      {variant === 'page' ? (
        <Group justify="space-between" align="center" wrap="wrap">
          {project ? (
            <Text size="sm" fw={500}>
              {project.name}
            </Text>
          ) : loadError ? (
            <Text size="sm" c="red">
              {loadError}
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              Загрузка…
            </Text>
          )}
          <Button component={Link} to={backHref} variant="default" size="sm">
            Назад к настройкам проекта
          </Button>
        </Group>
      ) : (
        <>
          {loadError ? (
            <Text size="sm" c="red">
              {loadError}
            </Text>
          ) : project ? (
            <Text size="sm" fw={500}>
              {project.name}
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              Загрузка…
            </Text>
          )}
        </>
      )}

      {settings && project ? (
        <Tabs value={activeTab} onChange={setTab} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="settings">Основные</Tabs.Tab>
            <Tabs.Tab value="admin-api">Admin API</Tabs.Tab>
            <Tabs.Tab value="content">Контент бота</Tabs.Tab>
            {showAdvancedTabs ? <Tabs.Tab value="link-replace">Замена ссылок</Tabs.Tab> : null}
            {showAdvancedTabs ? <Tabs.Tab value="exceptions">Исключения</Tabs.Tab> : null}
            {showAdvancedTabs ? <Tabs.Tab value="resources">AI ресурсы</Tabs.Tab> : null}
          </Tabs.List>
          <Group mt="xs" mb="xs">
            <Button
              size="compact-sm"
              variant="subtle"
              onClick={() => setShowAdvancedTabs((v) => !v)}
            >
              {showAdvancedTabs ? 'Скрыть расширенные вкладки' : 'Показать расширенные вкладки'}
            </Button>
          </Group>

          <Tabs.Panel value="settings" pt="md">
            <AiSettingsTab
              projectId={projectId}
              project={project}
              settings={settings}
              canEdit={canEdit}
              onSettingsUpdated={bump}
            />
          </Tabs.Panel>
          <Tabs.Panel value="admin-api" pt="md">
            <AiAdminApiActionsTab
              projectId={projectId}
              project={project}
              settings={settings}
              canEdit={canEdit}
              onSettingsUpdated={bump}
            />
          </Tabs.Panel>
          <Tabs.Panel value="content" pt="md">
            <AiContentTab
              projectId={projectId}
              projectAiId={projectAiId}
              canEdit={canEdit}
              refreshKey={refreshKey}
              settings={settings}
              onUpdated={bump}
              active={activeTab === 'content'}
            />
          </Tabs.Panel>
          <Tabs.Panel value="link-replace" pt="md">
            <AiLinkReplaceTab
              projectId={projectId}
              canEdit={canEdit}
              refreshKey={refreshKey}
              tabActive={activeTab === 'link-replace'}
              onContentUpdated={bump}
            />
          </Tabs.Panel>
          <Tabs.Panel value="exceptions" pt="md">
            <AiExceptionsTab projectId={projectId} canEdit={canEdit} refreshKey={refreshKey} />
          </Tabs.Panel>
          <Tabs.Panel value="resources" pt="md">
            <AiResourcesTab
              projectId={projectId}
              provider={provider}
              canEdit={canEdit}
              onResourcesChanged={bump}
            />
          </Tabs.Panel>
        </Tabs>
      ) : loadError ? null : (
        <Text size="sm" c="dimmed">
          Загрузка…
        </Text>
      )}
    </Stack>
  )
}
